import { type BubbleRoleDto, type UserDto } from "@crowdcoursing/api/openapi";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { type GridColDef } from "@mui/x-data-grid-pro";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";
import { useMemo, useRef, useState } from "react";

import { type ColumnData } from "~types/datagrid";
import { type DateRangeValue, PopoverDateRangePicker } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";

type ChartView = "growth" | "daily" | "roleDistribution" | "roleGrowth";

const CHART_VIEW_STORAGE_KEY = "userGrowthData.chartView";

const chartViewOptions: { label: string; value: ChartView }[] = [
  { label: "User Growth", value: "growth" },
  { label: "Daily Signups", value: "daily" },
  { label: "Role Distribution", value: "roleDistribution" },
  { label: "Role Growth Over Time", value: "roleGrowth" },
];

const defaultTitle = "User Growth";
const defaultSubtitle = "Track platform user growth over time";

export interface UserGrowthDataProps {
  title?: string;
  subtitle?: string;
  data: UserDto[] | null;
  status: Utils.QueryStatus;
  onTimeRangeChange?: (timeRange: DateRangeValue) => void;
  timeRange?: DateRangeValue;
}

interface DailyUserCount {
  date: string;
  count: number;
}

interface CumulativeUserCount {
  date: string;
  total: number;
}

const aggregateUsersByDay = (users: UserDto[]): DailyUserCount[] => {
  const countsByDay: Record<string, number> = {};

  users.forEach((user) => {
    if (user.timestamp) {
      const date = DateTime.fromISO(user.timestamp).toFormat("yyyy-MM-dd");
      countsByDay[date] = (countsByDay[date] || 0) + 1;
    }
  });

  return Object.entries(countsByDay)
    .map(([date, count]) => ({ count, date }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

const calculateCumulativeGrowth = (
  dailyCounts: DailyUserCount[],
  startDate?: string,
  endDate?: string,
): CumulativeUserCount[] => {
  if (!startDate || !endDate) {
    // Fallback to original behavior without date range
    let runningTotal = 0;
    return dailyCounts.map((day) => {
      runningTotal += day.count;
      return { date: day.date, total: runningTotal };
    });
  }

  // Calculate baseline: total users before the start date
  const baseline = dailyCounts.filter((d) => d.date < startDate).reduce((sum, d) => sum + d.count, 0);

  // Generate all dates in the range
  const start = DateTime.fromFormat(startDate, "yyyy-MM-dd");
  const end = DateTime.fromFormat(endDate, "yyyy-MM-dd");
  const totalDays = Math.floor(end.diff(start, "days").days) + 1;

  // Determine sampling interval based on range size
  // For ranges <= 60 days, show every day
  // For ranges <= 180 days, show every week
  // For ranges <= 730 days (2 years), show every 2 weeks
  // For larger ranges, show every month
  let interval = 1;
  if (totalDays > 730) {
    interval = 30;
  } else if (totalDays > 180) {
    interval = 14;
  } else if (totalDays > 60) {
    interval = 7;
  }

  const result: CumulativeUserCount[] = [];
  let currentDate = start;

  while (currentDate <= end) {
    const dateStr = currentDate.toFormat("yyyy-MM-dd");

    // Calculate cumulative total up to this date
    const cumulativeForDate =
      baseline +
      dailyCounts.filter((d) => d.date >= startDate && d.date <= dateStr).reduce((sum, d) => sum + d.count, 0);

    result.push({ date: dateStr, total: cumulativeForDate });
    currentDate = currentDate.plus({ days: interval });
  }

  // Always include the end date if not already included
  const lastDate = result[result.length - 1]?.date;
  if (lastDate !== endDate) {
    const finalTotal =
      baseline +
      dailyCounts.filter((d) => d.date >= startDate && d.date <= endDate).reduce((sum, d) => sum + d.count, 0);
    result.push({ date: endDate, total: finalTotal });
  }

  return result;
};

const getDateFormat = (dates: string[]): string => {
  if (dates.length === 0) {
    return "MMM d";
  }
  const years = new Set(dates.map((d) => DateTime.fromFormat(d, "yyyy-MM-dd").year));
  return years.size > 1 ? "MM/yyyy" : "MMM d";
};

// Generate display dates with smart sampling based on range size
const generateDisplayDates = (startDate?: string, endDate?: string): string[] => {
  if (!startDate || !endDate) {
    return [];
  }

  const start = DateTime.fromFormat(startDate, "yyyy-MM-dd");
  const end = DateTime.fromFormat(endDate, "yyyy-MM-dd");
  const totalDays = Math.floor(end.diff(start, "days").days) + 1;

  // Determine sampling interval based on range size
  let interval = 1;
  if (totalDays > 730) {
    interval = 30;
  } else if (totalDays > 180) {
    interval = 14;
  } else if (totalDays > 60) {
    interval = 7;
  }

  const result: string[] = [];
  let currentDate = start;

  while (currentDate <= end) {
    result.push(currentDate.toFormat("yyyy-MM-dd"));
    currentDate = currentDate.plus({ days: interval });
  }

  // Always include the end date if not already included
  if (result[result.length - 1] !== endDate) {
    result.push(endDate);
  }

  return result;
};

// Extract all bubble roles from users
const extractAllBubbleRoles = (users: UserDto[]): BubbleRoleDto[] => {
  const roles: BubbleRoleDto[] = [];
  users.forEach((user) => {
    if (user.bubbleRoles) {
      roles.push(...user.bubbleRoles);
    }
  });
  return roles;
};

// Aggregate by role name
interface RoleCount {
  role: string;
  count: number;
}

const aggregateByRole = (roles: BubbleRoleDto[]): RoleCount[] => {
  const counts: Record<string, number> = {};
  roles.forEach((r) => {
    const roleName = r.role || "Unknown";
    counts[roleName] = (counts[roleName] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([role, count]) => ({ count, role }))
    .sort((a, b) => b.count - a.count);
};

// Aggregate role growth over time
const aggregateRoleGrowthByDay = (
  roles: BubbleRoleDto[],
  startDate?: string,
  endDate?: string,
): { dates: string[]; series: { name: string; data: number[] }[] } => {
  // Get all unique roles
  const allRoles = new Set<string>();
  roles.forEach((r) => allRoles.add(r.role || "Unknown"));

  // Group by date
  const countsByDate: Record<string, Record<string, number>> = {};
  roles.forEach((r) => {
    if (r.timestamp) {
      const date = DateTime.fromISO(r.timestamp).toFormat("yyyy-MM-dd");
      const roleName = r.role || "Unknown";
      if (!countsByDate[date]) {
        countsByDate[date] = {};
      }
      countsByDate[date][roleName] = (countsByDate[date][roleName] || 0) + 1;
    }
  });

  // Sort all dates
  const sortedDates = Object.keys(countsByDate).sort();

  // Calculate baseline for each role (before start date)
  const baseline: Record<string, number> = {};
  allRoles.forEach((role) => (baseline[role] = 0));
  if (startDate) {
    sortedDates
      .filter((d) => d < startDate)
      .forEach((d) => {
        Object.entries(countsByDate[d]).forEach(([role, count]) => {
          baseline[role] = (baseline[role] || 0) + count;
        });
      });
  }

  // Generate display dates with smart sampling
  const displayDates = generateDisplayDates(startDate, endDate);
  if (displayDates.length === 0) {
    return { dates: [], series: [] };
  }

  // Build cumulative series for each display date
  const series: { name: string; data: number[] }[] = Array.from(allRoles).map((role) => ({
    data: [],
    name: role,
  }));

  displayDates.forEach((displayDate) => {
    // Calculate cumulative for each role up to this date
    series.forEach((s) => {
      const cumulativeForDate =
        (baseline[s.name] || 0) +
        sortedDates
          .filter((d) => d >= (startDate || "") && d <= displayDate)
          .reduce((sum, d) => sum + (countsByDate[d]?.[s.name] || 0), 0);
      s.data.push(cumulativeForDate);
    });
  });

  return { dates: displayDates, series };
};

const CumulativeGrowthChart = ({
  data,
  loading,
  timeRange,
}: {
  data: UserDto[];
  loading: boolean;
  timeRange?: DateRangeValue;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const startDateStr = timeRange?.startDate?.toFormat("yyyy-MM-dd");
  const endDateStr = timeRange?.endDate?.toFormat("yyyy-MM-dd");

  const dailyCounts = useMemo(() => aggregateUsersByDay(data), [data]);
  const cumulativeData = useMemo(
    () => calculateCumulativeGrowth(dailyCounts, startDateStr, endDateStr),
    [dailyCounts, endDateStr, startDateStr],
  );

  const dateFormat = useMemo(() => getDateFormat(cumulativeData.map((d) => d.date)), [cumulativeData]);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 350,
        type: "area",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        area: {
          color: "#3b82f6",
          fillOpacity: 0.3,
          lineWidth: 2,
          marker: {
            enabled: cumulativeData.length <= 30,
            radius: 3,
          },
        },
      },
      series: [
        {
          data: cumulativeData.map((d) => d.total),
          name: "Total Users",
          type: "area",
        },
      ],
      title: {
        text: "Cumulative User Growth",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = cumulativeData[index];
          const formattedDate = item?.date ? DateTime.fromFormat(item.date, "yyyy-MM-dd").toFormat("MMM d, yyyy") : "";
          return `<b>${formattedDate}</b><br/>Total Users: ${point.y}`;
        },
      },
      xAxis: {
        categories: cumulativeData.map((d) => DateTime.fromFormat(d.date, "yyyy-MM-dd").toFormat(dateFormat)),
        title: {
          text: "Date",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Total Users",
        },
      },
    }),
    [dateFormat, cumulativeData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (cumulativeData.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">No user data available for this period</Typography>
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />;
};

const DailySignupsChart = ({
  data,
  loading,
  timeRange,
}: {
  data: UserDto[];
  loading: boolean;
  timeRange?: DateRangeValue;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const startDateStr = timeRange?.startDate?.toFormat("yyyy-MM-dd");
  const endDateStr = timeRange?.endDate?.toFormat("yyyy-MM-dd");

  const allDailyCounts = useMemo(() => aggregateUsersByDay(data), [data]);

  // Generate all dates in range with counts (0 for days without signups)
  const dailyCounts = useMemo(() => {
    if (!startDateStr || !endDateStr) {
      // Fallback to filtered counts if no date range
      return allDailyCounts;
    }

    // Create a map of existing counts
    const countMap = new Map<string, number>();
    allDailyCounts.forEach((d) => countMap.set(d.date, d.count));

    // Generate all dates in the range
    const start = DateTime.fromFormat(startDateStr, "yyyy-MM-dd");
    const end = DateTime.fromFormat(endDateStr, "yyyy-MM-dd");
    const totalDays = Math.floor(end.diff(start, "days").days) + 1;

    // Determine sampling interval based on range size (same logic as cumulative)
    let interval = 1;
    if (totalDays > 730) {
      interval = 30;
    } else if (totalDays > 180) {
      interval = 14;
    } else if (totalDays > 60) {
      interval = 7;
    }

    const result: DailyUserCount[] = [];
    let currentDate = start;

    while (currentDate <= end) {
      const dateStr = currentDate.toFormat("yyyy-MM-dd");

      if (interval === 1) {
        // For daily view, show exact count for each day
        result.push({ count: countMap.get(dateStr) ?? 0, date: dateStr });
      } else {
        // For sampled view, aggregate counts within the interval
        const intervalEnd = currentDate.plus({ days: interval - 1 });
        const intervalEndStr = (intervalEnd <= end ? intervalEnd : end).toFormat("yyyy-MM-dd");
        let intervalCount = 0;
        for (const [d, count] of countMap.entries()) {
          if (d >= dateStr && d <= intervalEndStr) {
            intervalCount += count;
          }
        }
        result.push({ count: intervalCount, date: dateStr });
      }

      currentDate = currentDate.plus({ days: interval });
    }

    // Ensure end date is included if not already
    const lastDate = result[result.length - 1]?.date;
    if (lastDate !== endDateStr && interval > 1) {
      // Add any remaining counts for the final partial interval
      let finalCount = 0;
      for (const [d, count] of countMap.entries()) {
        if (d > lastDate && d <= endDateStr) {
          finalCount += count;
        }
      }
      result.push({ count: finalCount, date: endDateStr });
    }

    return result;
  }, [allDailyCounts, startDateStr, endDateStr]);

  const dateFormat = useMemo(() => getDateFormat(dailyCounts.map((d) => d.date)), [dailyCounts]);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 350,
        type: "column",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          color: "#10b981",
        },
      },
      series: [
        {
          data: dailyCounts.map((d) => d.count),
          name: "New Users",
          type: "column",
        },
      ],
      title: {
        text: "Daily User Signups",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = dailyCounts[index];
          const formattedDate = item?.date ? DateTime.fromFormat(item.date, "yyyy-MM-dd").toFormat("MMM d, yyyy") : "";
          return `<b>${formattedDate}</b><br/>New Users: ${point.y}`;
        },
      },
      xAxis: {
        categories: dailyCounts.map((d) => DateTime.fromFormat(d.date, "yyyy-MM-dd").toFormat(dateFormat)),
        title: {
          text: "Date",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "New Users",
        },
      },
    }),
    [dailyCounts, dateFormat],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />;
};

interface RoleDistributionRow {
  rowId: string;
  name: string;
  count: number;
  percentage: number;
}

const RoleDistributionChart = ({ data, loading }: { data: UserDto[]; loading: boolean }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const allRoles = useMemo(() => extractAllBubbleRoles(data), [data]);
  const roleData = useMemo(() => aggregateByRole(allRoles), [allRoles]);

  // Calculate total for percentage
  const total = useMemo(() => roleData.reduce((sum, item) => sum + item.count, 0), [roleData]);

  // Create rows for DataGrid
  const rows: RoleDistributionRow[] = useMemo(
    () =>
      roleData.map((item) => ({
        count: item.count,
        name: item.role,
        percentage: total > 0 ? Math.round((item.count / total) * 1000) / 10 : 0,
        rowId: item.role,
      })),
    [roleData, total],
  );

  const columns = useMemo<GridColDef<RoleDistributionRow>[]>(
    () => [
      {
        field: "name",
        flex: 2,
        headerName: "Name",
        minWidth: 150,
      },
      {
        align: "right",
        field: "count",
        flex: 1,
        headerAlign: "right",
        headerName: "Count",
        minWidth: 80,
      },
      {
        align: "right",
        field: "percentage",
        flex: 1,
        headerAlign: "right",
        headerName: "%",
        minWidth: 70,
        valueFormatter: ({ value }: { value: number }) => `${value}%`,
      },
    ],
    [],
  );

  const pieData = useMemo(
    () =>
      roleData.map((d) => ({
        name: d.role,
        y: d.count,
      })),
    [roleData],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 350,
        type: "pie",
      },
      credits: {
        enabled: false,
      },
      legend: {
        align: "right",
        enabled: true,
        layout: "vertical",
        verticalAlign: "middle",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      series: [
        {
          data: pieData,
          name: "Memberships",
          type: "pie",
        },
      ],
      title: {
        text: "Role Distribution",
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> memberships ({point.percentage:.1f}%)",
      },
    }),
    [pieData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (roleData.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">No role data available</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Pie Chart - Left Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
      </Grid>

      {/* DataGrid Table - Right Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>
          <DataGrid<RoleDistributionRow>
            columns={columns as ColumnData<RoleDistributionRow>[]}
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="client"
            rowHeight={40}
            rows={rows}
            slotProps={{
              container: {
                sx: { height: "auto", maxHeight: 400, minHeight: "unset" },
              },
              empty: {
                message: "No data available",
              },
            }}
            slots={{
              toolbar: () => null,
            }}
            sortingMode="client"
            status={loading ? "pending" : "success"}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

const RoleGrowthChart = ({
  data,
  loading,
  timeRange,
}: {
  data: UserDto[];
  loading: boolean;
  timeRange?: DateRangeValue;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const startDateStr = timeRange?.startDate?.toFormat("yyyy-MM-dd");
  const endDateStr = timeRange?.endDate?.toFormat("yyyy-MM-dd");

  const allRoles = useMemo(() => extractAllBubbleRoles(data), [data]);
  const growthData = useMemo(
    () => aggregateRoleGrowthByDay(allRoles, startDateStr, endDateStr),
    [allRoles, endDateStr, startDateStr],
  );

  const dateFormat = useMemo(() => getDateFormat(growthData.dates), [growthData.dates]);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 350,
        type: "line",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          marker: {
            enabled: growthData.dates.length <= 30,
            radius: 3,
          },
        },
      },
      series: growthData.series.map((s) => ({
        data: [...s.data],
        name: s.name,
        type: "line" as const,
      })),
      title: {
        text: "Role Growth Over Time",
      },
      tooltip: {
        formatter: function () {
          const points = this.points?.filter((p) => (p.y ?? 0) > 0).sort((a, b) => (b.y ?? 0) - (a.y ?? 0)) ?? [];
          if (points.length === 0) {
            return false;
          }
          let html = `<b>${this.x}</b><br/>`;
          points.forEach((p) => {
            html += `<span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${p.y}</b><br/>`;
          });
          return html;
        },
        shared: true,
        useHTML: true,
      },
      xAxis: {
        categories: growthData.dates.map((d) => DateTime.fromFormat(d, "yyyy-MM-dd").toFormat(dateFormat)),
        title: {
          text: "Date",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Total Memberships",
        },
      },
    }),
    [dateFormat, growthData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (growthData.dates.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">No role data available for this period</Typography>
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />;
};

const getInitialChartView = (): ChartView => {
  const stored = localStorage.getItem(CHART_VIEW_STORAGE_KEY);
  if (stored === "growth" || stored === "daily" || stored === "roleDistribution" || stored === "roleGrowth") {
    return stored;
  }
  return "growth";
};

export const UserGrowthData = ({
  title = defaultTitle,
  subtitle = defaultSubtitle,
  data,
  status,
  onTimeRangeChange,
  timeRange,
}: UserGrowthDataProps) => {
  const [chartView, setChartView] = useState<ChartView>(getInitialChartView);
  const loading = status === "pending";
  const error = status === "error";

  const defaultTimeRange: DateRangeValue = {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ days: 30 }),
  };

  const handleTimeRangeChange = (newRange: DateRangeValue) => {
    onTimeRangeChange?.(newRange);
  };

  const handleChartViewChange = (event: SelectChangeEvent) => {
    const newView = event.target.value as ChartView;
    setChartView(newView);
    localStorage.setItem(CHART_VIEW_STORAGE_KEY, newView);
  };

  const totalUsers = data?.length ?? 0;

  // Calculate cumulative growth summary for the caption
  const growthSummary = useMemo(() => {
    if (!data || data.length === 0 || !timeRange?.startDate || !timeRange?.endDate) {
      return null;
    }

    const startDateStr = timeRange.startDate.toFormat("yyyy-MM-dd");
    const endDateStr = timeRange.endDate.toFormat("yyyy-MM-dd");

    const dailyCounts = aggregateUsersByDay(data);
    const cumulativeData = calculateCumulativeGrowth(dailyCounts, startDateStr, endDateStr);

    if (cumulativeData.length === 0) {
      return null;
    }

    const startingUsers = cumulativeData[0]?.total ?? 0;
    const endingUsers = cumulativeData[cumulativeData.length - 1]?.total ?? 0;
    const percentageIncrease = startingUsers > 0 ? ((endingUsers - startingUsers) / startingUsers) * 100 : 0;

    return {
      endDate: timeRange.endDate.toFormat("MMM d, yyyy"),
      endingUsers,
      percentageIncrease,
      startDate: timeRange.startDate.toFormat("MMM d, yyyy"),
      startingUsers,
    };
  }, [data, timeRange]);

  const renderChart = () => {
    switch (chartView) {
      case "growth":
        return <CumulativeGrowthChart data={data ?? []} loading={loading} timeRange={timeRange} />;
      case "daily":
        return <DailySignupsChart data={data ?? []} loading={loading} timeRange={timeRange} />;
      case "roleDistribution":
        return <RoleDistributionChart data={data ?? []} loading={loading} />;
      case "roleGrowth":
        return <RoleGrowthChart data={data ?? []} loading={loading} timeRange={timeRange} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 2, pt: 1 }}>
      {/* How to Read This Data */}
      <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, mb: 3, p: 2 }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          How to Read This Data
        </Typography>
        <Typography color="text.secondary" variant="body2">
          <strong>User Growth:</strong> Shows the cumulative total of users over time - the running total of all
          registered users.
          <br />
          <strong>Daily Signups:</strong> Shows how many new users signed up each day within the selected date range.
          <br />
          <strong>Role Distribution:</strong> Shows the breakdown of users by their roles (Volunteer, Educator, etc.) as
          a pie chart.
          <br />
          <strong>Role Growth Over Time:</strong> Tracks how each role category has grown over the selected period.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <Typography sx={{ mb: 0.5 }} variant="h6">
                {title}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {subtitle}
              </Typography>
            </Box>
            <Box sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="user-growth-chart-view-label">View</InputLabel>
                <Select
                  id="user-growth-chart-view-select"
                  label="View"
                  labelId="user-growth-chart-view-label"
                  onChange={handleChartViewChange}
                  value={chartView}
                >
                  {chartViewOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ width: 240 }}>
                <PopoverDateRangePicker
                  onChange={handleTimeRangeChange}
                  size="small"
                  value={timeRange ?? defaultTimeRange}
                />
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid size={12}>
          {error ? (
            <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
              <Typography color="error">Failed to load user data</Typography>
            </Box>
          ) : (
            renderChart()
          )}
        </Grid>

        <Grid size={12}>
          <Typography color="text.secondary" variant="caption">
            {growthSummary
              ? `From ${growthSummary.startDate} to ${growthSummary.endDate}, we started with ${growthSummary.startingUsers} users and grew to ${growthSummary.endingUsers} users, a ${growthSummary.percentageIncrease.toFixed(1)}% increase`
              : `${totalUsers} users in selected period`}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
